import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building2, Users, Key, GraduationCap, Mail } from 'lucide-react';
import { getUser, authenticatedFetch } from '../lib/auth';
import { socket } from '../lib/socket';
import MemberManagement from './MemberManagement';
import UserManagement from './UserManagement';
import APICredentialsManagement from './APICredentialsManagement';
import SensorManagement from './SensorManagement';
import MemberOrgUserManagement from './MemberOrgUserManagement';
import MemberEmailDomainManagement from './MemberEmailDomainManagement';

function TabBadge({ count }: { count: number }) {
  if (!count || count <= 0) return null;
  return (
    <span
      className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold text-white bg-red-500 rounded-full"
      aria-label={`${count} varsel`}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

type Tab = 'members' | 'users' | 'sensors' | 'api' | 'org-users' | 'email-domains';

export default function MembersAndUsers() {
  const currentUser = getUser();
  const canManageUsers = currentUser?.role === 'admin' || currentUser?.role === 'systemadmin';
  const canManageAPI = currentUser?.role === 'admin' || currentUser?.role === 'systemadmin';
  const isMemberAdmin = currentUser?.role === 'member' && (currentUser as any)?.is_member_admin === true;

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as Tab | null;
  const memberIdParam = searchParams.get('member_id');

  const validTabs: Tab[] = ['members', 'users', 'sensors', 'api', 'org-users', 'email-domains'];
  const [activeTab, setActiveTab] = useState<Tab>(
    tabParam && validTabs.includes(tabParam) ? tabParam : 'members'
  );
  const [filterMemberId, setFilterMemberId] = useState<string | null>(memberIdParam);

  // Badge-tellere per sub-fane. Varsel på hovedmenyen "Organisasjoner" skal
  // alltid speiles på rett sub-fane så bruker ser hvor varselet stammer fra.
  const [pendingUsersCount, setPendingUsersCount] = useState(0);        // users (admin/sysadmin)
  const [pendingOrgInvitationsCount, setPendingOrgInvitationsCount] = useState(0); // org-users (medlem-admin)
  // Framtidige tellere kan legges til her: pendingMembersCount, pendingSensorsCount, pendingApiCount

  useEffect(() => {
    if (tabParam && validTabs.includes(tabParam)) {
      setActiveTab(tabParam);
    }
    setFilterMemberId(memberIdParam);
  }, [tabParam, memberIdParam]);

  // Admin/systemadmin: pending brukere som venter på godkjenning (speiler Layout.tsx)
  useEffect(() => {
    if (!canManageUsers || !currentUser?.id) return;

    const fetchPendingCount = async () => {
      try {
        const response = await authenticatedFetch('/api/admin/pending-users-count', {
          method: 'POST',
          body: JSON.stringify({ admin_id: currentUser.id }),
        });
        const result = await response.json();
        if (result.success) setPendingUsersCount(result.count);
      } catch (err) {
        console.error('[MembersAndUsers] Failed to fetch pending users count:', err);
      }
    };

    fetchPendingCount();

    const handleChange = () => fetchPendingCount();
    socket.on('user_pending_approval', handleChange);
    socket.on('user_approved', handleChange);
    return () => {
      socket.off('user_pending_approval', handleChange);
      socket.off('user_approved', handleChange);
    };
  }, [canManageUsers, currentUser?.id]);

  // Medlem-admin: antall sendte invitasjoner som ikke er besvart ennå
  useEffect(() => {
    if (!isMemberAdmin || !currentUser?.member_id) return;

    const fetchOrgInvitations = async () => {
      try {
        const r = await authenticatedFetch(`/api/admin/members/${currentUser.member_id}/users`);
        if (!r.ok) return;
        const d = await r.json();
        const invitations = d.pending_invitations || [];
        setPendingOrgInvitationsCount(invitations.length);
      } catch (err) {
        console.error('[MembersAndUsers] Failed to fetch org invitations:', err);
      }
    };

    fetchOrgInvitations();
    const interval = setInterval(fetchOrgInvitations, 60000);
    return () => clearInterval(interval);
  }, [isMemberAdmin, currentUser?.member_id]);

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    if (tab !== 'users') {
      setFilterMemberId(null);
      setSearchParams({ tab });
    } else {
      if (filterMemberId) {
        setSearchParams({ tab, member_id: filterMemberId });
      } else {
        setSearchParams({ tab });
      }
    }
  };

  const clearMemberFilter = () => {
    setFilterMemberId(null);
    setSearchParams({ tab: 'users' });
  };

  // Deklarativ liste over tabs — hver tab bærer sin egen badge-telling.
  // Hvis nye pending-tellere kommer senere (f.eks. for sensorer, API, medlemsorg),
  // oppdateres `badge`-feltet her og hovedmeny-badgen i Layout.tsx får automatisk
  // samme kilde å peke på.
  type TabDef = {
    id: Tab;
    label: string;
    icon: React.ReactNode;
    visible: boolean;
    badge: number;
  };

  const tabs: TabDef[] = [
    {
      id: 'members',
      label: currentUser?.role === 'member' ? 'Min organisasjon' : 'Medlemsorganisasjoner',
      icon: <Building2 size={18} />,
      visible: true,
      badge: 0, // Ingen pending-flyt for medlemsorganisasjoner i dag
    },
    {
      id: 'org-users',
      label: 'Brukere',
      icon: <Users size={18} />,
      visible: isMemberAdmin,
      badge: pendingOrgInvitationsCount, // Ventende invitasjoner sendt av medlem-admin
    },
    {
      id: 'email-domains',
      label: 'E-postdomener',
      icon: <Mail size={18} />,
      visible: isMemberAdmin,
      badge: 0, // Domeneforslag krever ingen handling fra medlem-admin
    },
    {
      id: 'users',
      label: 'Brukere',
      icon: <Users size={18} />,
      visible: canManageUsers,
      badge: pendingUsersCount, // Brukere som venter på admin-godkjenning
    },
    {
      id: 'sensors',
      label: 'Sensorer',
      icon: <GraduationCap size={18} />,
      visible: canManageUsers,
      badge: 0, // Ingen pending-flyt for sensorer i dag
    },
    {
      id: 'api',
      label: 'API',
      icon: <Key size={18} />,
      visible: canManageAPI,
      badge: 0, // Ingen pending-flyt for API-credentials i dag
    },
  ];

  const visibleTabs = tabs.filter((t) => t.visible);

  const tabBtn = (active: boolean) =>
    `px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
      active
        ? 'border-kf-primary text-kf-primary'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }`;

  return (
    <div className="space-y-6">
      {visibleTabs.length > 1 && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="border-b">
            <nav className="flex -mb-px overflow-x-auto">
              {visibleTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={tabBtn(activeTab === tab.id)}
                >
                  {tab.icon}
                  {tab.label}
                  <TabBadge count={tab.badge} />
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'members' && <MemberManagement />}
      {activeTab === 'users' && canManageUsers && (
        <UserManagement filterMemberId={filterMemberId} onClearFilter={clearMemberFilter} />
      )}
      {activeTab === 'sensors' && canManageUsers && <SensorManagement />}
      {activeTab === 'api' && canManageAPI && <APICredentialsManagement />}
      {activeTab === 'org-users' && isMemberAdmin && currentUser?.member_id && (
        <MemberOrgUserManagement memberId={currentUser.member_id} />
      )}
      {activeTab === 'email-domains' && isMemberAdmin && currentUser?.member_id && (
        <MemberEmailDomainManagement memberId={currentUser.member_id} />
      )}
    </div>
  );
}
